import { useState, useEffect } from "react";
import { Button, Container, Stack, Typography, TextField, Paper, Alert, Divider } from "@mui/material";
import Page from "src/components/Page";
import FormItem from "src/components/form-item/FormItem";
import { asyncRequest } from "src/services/network";
import { alert } from "src/services/alert";

/**
 * 金银通释放控制页面
 * 分为静态释放（定时任务配置）和动态释放（兑换权益金配置）两个部分
 */
export default function ReleaseControl() {
  const [dynamic, setDynamic] = useState({
    xfqRatio: 0.7,
    ptcvRatio: 0,
    dhqRatio: 0.3,
    ptcvRetention: 0.5,
  });

  const [staticCfg, setStaticCfg] = useState({
    xfqRatio: 0.7,
    ptcvRatio: 0,
    dhqRatio: 0.3,
    ptcvRetention: 0.5,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  async function fetchConfig() {
    try {
      const { data } = await asyncRequest({
        method: "GET",
        path: "/system/config/release-control/query",
      });
      if (data) {
        if (data.dynamic) setDynamic(data.dynamic);
        if (data.static) setStaticCfg(data.static);
      }
    } catch (e) {
      console.error("获取释放控制配置失败:", e);
    }
  }

  // 校验比例之和是否为 100%
  function validateRatio(xfq, ptcv, dhq) {
    const total = Number(xfq) + Number(ptcv) + Number(dhq);
    return Math.abs(total - 1) < 0.0001;
  }

  // 动态释放比例之和
  const dynamicTotal = Number(dynamic.xfqRatio) + Number(dynamic.ptcvRatio) + Number(dynamic.dhqRatio);
  const dynamicValid = Math.abs(dynamicTotal - 1) < 0.0001;

  // 静态释放比例之和
  const staticTotal = Number(staticCfg.xfqRatio) + Number(staticCfg.ptcvRatio) + Number(staticCfg.dhqRatio);
  const staticValid = Math.abs(staticTotal - 1) < 0.0001;

  // 拼团贡献值公司留存校验
  const dynamicRetentionValid = Number(dynamic.ptcvRetention) >= 0 && Number(dynamic.ptcvRetention) <= 1;
  const staticRetentionValid = Number(staticCfg.ptcvRetention) >= 0 && Number(staticCfg.ptcvRetention) <= 1;

  async function handleSave() {
    if (!dynamicValid) {
      alert(`动态释放比例之和必须为100%，当前为${(dynamicTotal * 100).toFixed(2)}%`);
      return;
    }
    if (!staticValid) {
      alert(`静态释放比例之和必须为100%，当前为${(staticTotal * 100).toFixed(2)}%`);
      return;
    }
    if (!dynamicRetentionValid) {
      alert("动态释放-拼团贡献值公司留存比例必须在0%~100%之间");
      return;
    }
    if (!staticRetentionValid) {
      alert("静态释放-拼团贡献值公司留存比例必须在0%~100%之间");
      return;
    }

    setLoading(true);
    try {
      const { msg } = await asyncRequest({
        method: "POST",
        path: "/system/config/release-control/save",
        data: {
          dynamic,
          static: staticCfg,
        },
      });
      alert(msg, "success");
    } catch (e) {
      console.error("保存释放控制配置失败:", e);
    } finally {
      setLoading(false);
    }
  }

  function renderRatioSection(title, sectionType, config, setConfig, total, isValid, retentionValid) {
    const prefix = sectionType;
    return (
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <FormItem labelStyle={{ width: 200 }} title="权益金比例" required>
          <TextField
            type="number"
            autoComplete="off"
            size="small"
            value={config.xfqRatio}
            onChange={(e) => setConfig({ ...config, xfqRatio: e.target.value })}
            inputProps={{ step: 0.01, min: 0, max: 1 }}
            sx={{ width: 200 }}
          />
          <Typography component="span" ml={1} fontSize={14} color="text.secondary">
            （{(Number(config.xfqRatio) * 100).toFixed(0)}%）
          </Typography>
        </FormItem>

        <FormItem labelStyle={{ width: 200 }} title="拼团贡献值比例" required>
          <TextField
            type="number"
            autoComplete="off"
            size="small"
            value={config.ptcvRatio}
            onChange={(e) => setConfig({ ...config, ptcvRatio: e.target.value })}
            inputProps={{ step: 0.01, min: 0, max: 1 }}
            sx={{ width: 200 }}
          />
          <Typography component="span" ml={1} fontSize={14} color="text.secondary">
            （{(Number(config.ptcvRatio) * 100).toFixed(0)}%）
          </Typography>
        </FormItem>

        <FormItem labelStyle={{ width: 200 }} title="兑换券比例" required>
          <TextField
            type="number"
            autoComplete="off"
            size="small"
            value={config.dhqRatio}
            onChange={(e) => setConfig({ ...config, dhqRatio: e.target.value })}
            inputProps={{ step: 0.01, min: 0, max: 1 }}
            sx={{ width: 200 }}
          />
          <Typography component="span" ml={1} fontSize={14} color="text.secondary">
            （{(Number(config.dhqRatio) * 100).toFixed(0)}%）
          </Typography>
        </FormItem>

        {!isValid && (
          <Alert severity="error" sx={{ ml: 25, mb: 1 }}>
            比例之和必须为100%，当前为 {(total * 100).toFixed(2)}%
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <FormItem labelStyle={{ width: 200 }} title="拼团贡献值公司留存" required>
          <TextField
            type="number"
            autoComplete="off"
            size="small"
            value={config.ptcvRetention}
            onChange={(e) => setConfig({ ...config, ptcvRetention: e.target.value })}
            inputProps={{ step: 0.01, min: 0, max: 1 }}
            sx={{ width: 200 }}
          />
          <Typography component="span" ml={1} fontSize={14} color="text.secondary">
            （{(Number(config.ptcvRetention) * 100).toFixed(0)}%，公司留存部分不入拼团资金池）
          </Typography>
        </FormItem>

        {!retentionValid && (
          <Alert severity="error" sx={{ ml: 25, mb: 1 }}>
            公司留存比例必须在0%~100%之间
          </Alert>
        )}

        <Alert severity="info" sx={{ ml: 25, mt: 1 }}>
          拼团贡献值规则：产出金豆×拼团贡献值比例 → 扣除公司留存 → 剩余进拼团资金池 → 给兑换用户入池金额50%等值的拼团贡献值
        </Alert>
      </Paper>
    );
  }

  return (
    <Page title="释放控制">
      <Container maxWidth="md">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            释放控制
          </Typography>
        </Stack>

        {renderRatioSection(
          "静态释放（定时任务配置）",
          "static",
          staticCfg,
          setStaticCfg,
          staticTotal,
          staticValid,
          staticRetentionValid
        )}

        {renderRatioSection(
          "动态释放（兑换权益金配置）",
          "dynamic",
          dynamic,
          setDynamic,
          dynamicTotal,
          dynamicValid,
          dynamicRetentionValid
        )}

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={2}>
          <Button variant="outlined" onClick={fetchConfig}>
            重置
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || !dynamicValid || !staticValid || !dynamicRetentionValid || !staticRetentionValid}
          >
            保存配置
          </Button>
        </Stack>
      </Container>
    </Page>
  );
}
